"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import {
  useCurrentScore,
  useUserBadges,
  useUserStreak,
} from "@/hooks/useGamification";
import { BadgeDefinition, UserStreak } from "@/lib/gamification";

import { showBadgeEarnedNotification } from "./gamification/BadgeEarnedNotification";
import { showScoreIncreaseNotification } from "./gamification/ScoreNotification";
import { showStreakMilestoneNotification } from "./gamification/StreakMilestoneNotification";

interface GamificationContextType {
  currentBadges?: BadgeDefinition[];
  currentScore?: { level: number; score: number; };
  currentStreak?: UserStreak;
}

interface GamificationProviderProps {
  children: React.ReactNode;
}

const GamificationContext = createContext<GamificationContextType | null>(null);

export function GamificationProvider({ children }: GamificationProviderProps) {
  const { data: currentScore } = useCurrentScore();
  const { data: currentStreak } = useUserStreak();
  const { data: currentBadges } = useUserBadges();

  // Refs to track previous values for comparison
  const oldScore = useRef(currentScore);
  const oldStreak = useRef(currentStreak);
  const oldBadges = useRef(currentBadges);

  const contextValue: GamificationContextType = useMemo(
    () => ({
      currentBadges,
      currentScore,
      currentStreak: currentStreak || undefined,
    }),
    [currentScore, currentStreak, currentBadges]
  );

  // Score change detection
  useEffect(() => {
    if (oldScore.current?.score === undefined) {
      if (currentScore?.score !== undefined) {
        oldScore.current = { ...currentScore };
      }
    } else if (
      currentScore &&
      (currentScore.score !== oldScore.current.score ||
        currentScore.level !== oldScore.current.level)
    ) {
      const pointsEarned = currentScore.score - oldScore.current.score;
      showScoreIncreaseNotification(
        pointsEarned,
        currentScore,
        oldScore.current
      );
      oldScore.current = { ...currentScore };
    }
  }, [currentScore]);

  // Streak change detection
  useEffect(() => {
    if (oldStreak.current?.current_streak === undefined) {
      if (currentStreak?.current_streak !== undefined) {
        oldStreak.current = { ...currentStreak };
      }
    } else if (
      currentStreak &&
      currentStreak.current_streak > oldStreak.current.current_streak
    ) {
      // Show streak milestone notification for significant milestones
      const newStreak = currentStreak.current_streak;
      showStreakMilestoneNotification(newStreak);

      oldStreak.current = { ...currentStreak };
    }
  }, [currentStreak]);

  // Badge change detection
  useEffect(() => {
    if (!oldBadges.current) {
      if (currentBadges) {
        oldBadges.current = [...currentBadges];
      }
    } else if (
      currentBadges &&
      currentBadges.length > oldBadges.current.length
    ) {
      // Find newly earned badges by comparing arrays
      const oldBadgeSlugs = new Set(oldBadges.current.map(({ slug }) => slug));
      const newBadges = currentBadges.filter(
        ({ slug }) => !oldBadgeSlugs.has(slug)
      );

      newBadges.forEach((badge) => {
        showBadgeEarnedNotification(badge);
      });

      oldBadges.current = [...currentBadges];
    }
  }, [currentBadges]);

  return (
    <GamificationContext.Provider value={contextValue}>
      {children}
    </GamificationContext.Provider>
  );
}

export function useGamification() {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error(
      "useGamification must be used within a GamificationProvider"
    );
  }
  return context;
}
